import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import avatarGrey from 'assets/images/defaultAvatar-grey.png';
import avatarWhite from 'assets/images/defaultAvatar-white.svg';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import calendar from 'assets/icons/calendar-check-02.svg';
import { ErrorToast, SuccessToast } from 'utils/Toasts';
import EditMatchResultModal from 'components/EditResultModal';
import { get, getUserRoleFromToken } from 'api';
import { Edit } from '@mui/icons-material';
import { getPlayoffPairs } from 'utils/tournamentHelper';

const TournamentPlayerCard = ({
  pRez,
  first_name,
  last_name,
  winner,
  user_id,
  doubles_partner,
  partner_first_name,
  partner_last_name,
  isChallenger,
  avatar,
  fetchMainTable,
  matchKey,
  playerGroup,
  fullMatchKey,
  fullRow,
  allPlayers,
  tournament_gender,
  tournament_level,
  tournament_stage,
  tournament_id,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const rez = pRez ? pRez : '';
  const firstName = first_name !== null ? first_name : '';
  const lastName = last_name !== null ? last_name : '';
  const partnerName = partner_first_name !== null ? partner_first_name : '';
  const partnerLastName = partner_last_name !== null ? partner_last_name : '';
  const fullName = `${firstName} ${lastName}`;
  const partnerFullName = `${partnerName} ${partnerLastName}`;
  const isWinnerWithPartner = winner && winner.includes(',');
  const winners = isWinnerWithPartner && winner ? winner.split(',') : [winner];
  const isWinner = winners.includes(user_id);
  const [showTooltip, setShowTooltip] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const isAdmin = getUserRoleFromToken() === 'admin' ? true : false;
  const matchPairs = getPlayoffPairs();

  const getMatchInfoForGroup = (groupName) => {
    return matchPairs.find(
      (pair) => pair.group1 === groupName || pair.group2 === groupName,
    );
  };

  const matchInfo = useMemo(() => {
    if (!playerGroup) return null;
    return getMatchInfoForGroup(playerGroup);
  }, [playerGroup]);

  const opponent = useMemo(() => {
    if (!matchInfo || !matchInfo.matchKey) return null;
    return allPlayers?.find(
      (p) =>
        p[`${matchInfo.matchKey}_group`] ===
        (playerGroup === matchInfo.group1
          ? matchInfo.group2
          : matchInfo.group1),
    );
  }, [allPlayers, matchInfo, playerGroup]);

  const reverseScore = (result) => {
    if (!result) return '';
    return result
      .split(' ')
      .map((set) => {
        const [a, b] = set.split(':');
        return b && a ? `${b}:${a}` : set;
      })
      .join(' ');
  };

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      classes={{ popper: className }}
    />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.base.white,
      color: theme.palette.darkGrey[700],
      boxShadow:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      padding: '8px 12px',
      borderRadius: '8px',
    },
  }));

  const openEditModal = (match) => {
    setSelectedMatch(match);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedMatch(null);
    setIsModalOpen(false);
  };

  const splitMatchResult = (result, isChallenger) => {
    const sets = result.split(' ');
    const scores = sets.map((set) => set.split(':'));

    const playerScores = scores.map((score) => {
      const playerScore = isChallenger ? score[0] : score[1];
      return playerScore;
    });

    return playerScores;
  };

  const isWinnerPlayerA = (rez) => {
    if (rez === null) {
      return false;
    }
    const sets = rez.split(' ');
    let playerWins = 0;
    let opponentWins = 0;
    sets.forEach((set) => {
      const scores = set.split(':');
      const playerScore = parseInt(scores[0]);
      const opponentScore = parseInt(scores[1]);
      if (playerScore > opponentScore) {
        playerWins++;
      } else if (playerScore < opponentScore) {
        opponentWins++;
      }
    });
    return playerWins > opponentWins;
  };

  const updatedRez = (rez) => {
    const parts = rez.split(' ');
    const regex = /\((\d+)\)/g;
    const superscriptParts = parts.map((part) =>
      part.replace(regex, '<sup>$1</sup>'),
    );
    const superscriptRezultatas = superscriptParts.join(' ');
    return superscriptRezultatas;
  };

  const playerScores = splitMatchResult(rez, isChallenger);

  return (
    <>
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <EditMatchResultModal
        open={isModalOpen}
        onClose={closeEditModal}
        match={selectedMatch}
        fetchMainTable={fetchMainTable}
        successToastRef={successToastRef}
        errorToastRef={errorToastRef}
        openedFrom="playoffs"
      />
      <Box
        sx={{
          width: '304px',
          height: '48px',
          padding: '8px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: isWinner ? '8px' : '0px',
          backgroundColor: isWinner
            ? theme.palette.darkGrey[100]
            : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component={'img'}
            src={
              avatar
                ? `https://backend.tenisopartneris.lt/public/images/${avatar}`
                : isWinner
                ? avatarWhite
                : avatarGrey
            }
            alt="avatar"
            sx={{
              width: '32px',
              height: '32px',
              objectFit: 'cover',
              borderRadius: '50%',
              marginRight: '8px',
            }}
          />
          <Box
            component={'a'}
            sx={{
              display: 'flex',
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                textDecorationColor: theme.palette.darkGrey[700],
              },
            }}
            href={`${window.location.origin}/info/${user_id}`}
          >
            <Typography
              variant={'body4'}
              fontWeight={600}
              textAlign={'left'}
              color={theme.palette.darkGrey[700]}
            >
              {fullName}
            </Typography>
          </Box>
          <Box
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => navigate(`/info/${doubles_partner}`)}
          >
            <Typography
              variant={'body4'}
              fontWeight={600}
              color={theme.palette.darkGrey[700]}
            >
              {partnerFullName}
            </Typography>
          </Box>
        </Box>
        <Box>
          {playerScores[0].includes('202') ? (
            <LightTooltip
              title={rez}
              placement={'top'}
              open={showTooltip[`${user_id}`] || false}
              onOpen={() =>
                setShowTooltip((prevState) => ({
                  ...prevState,
                  [`${user_id}`]: true,
                }))
              }
              onClose={() =>
                setShowTooltip((prevState) => ({
                  ...prevState,
                  [`${user_id}`]: false,
                }))
              }
            >
              <Box
                component={'img'}
                src={calendar}
                marginLeft={'8px'}
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  setShowTooltip((prevState) => ({
                    ...prevState,
                    [`${user_id}`]: !prevState[`${user_id}`],
                  }))
                }
                alt="calendar icon"
              />
            </LightTooltip>
          ) : (
            playerScores
              .filter((score) => score.trim() !== '')
              .map((score, index) => {
                return (
                  <Typography
                    key={index}
                    variant={'body4'}
                    fontWeight={600}
                    color={
                      isWinner
                        ? theme.palette.darkGrey[700]
                        : theme.palette.darkGrey[600]
                    }
                    dangerouslySetInnerHTML={{
                      __html: updatedRez(score),
                    }}
                    sx={{
                      '&:not(:last-child)': {
                        marginRight: '20px',
                      },
                    }}
                  />
                );
              })
          )}
        </Box>
        {isAdmin && (
          <Edit
            sx={{
              ml: '4px',
              fontSize: '16px',
              color: theme.palette.grey[500],
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
            onClick={async () => {
              try {
                if (!matchInfo || !matchInfo.matchKey || !opponent) {
                  errorToastRef.current?.showError(
                    'Nepavyko nustatyti poros redagavimui',
                  );
                  return;
                }

                // Fetch the match to identify the challenger
                const query = new URLSearchParams({
                  matchKey: `${matchInfo.matchKey}_rez`,
                  tournament_id,
                  tournament_level,
                  tournament_gender,
                  tournament_stage,
                  playerA_id: fullRow.user_id,
                  playerB_id: opponent.user_id,
                }).toString();

                const response = await get(
                  `/matches/tournament-match?${query}`,
                );
                const matchData = await response.json();

                // determine real challenger
                const isChallenger =
                  matchData.challenger_id === parseInt(fullRow.user_id);
                const challenger = isChallenger ? fullRow : opponent;
                const opponentPlayer = isChallenger ? opponent : fullRow;
                const resultFromA = challenger[matchInfo.matchKey + '_rez'];
                const resultFromB = opponentPlayer[matchInfo.matchKey + '_rez'];
                
                // Check if results are actual scores vs planned match info
                const isActualScore = (str) => {
                  if (!str) return false;
                  // Planned info has parentheses, dates, or is too long
                  if (str.includes('(') || str.includes('-') || str.length > 20) return false;
                  // Actual scores have pattern like "6:4"
                  return /\d+:\d+/.test(str);
                };
                
                const hasResult = isActualScore(resultFromA) && isActualScore(resultFromB);
                const isWinner = hasResult
                  ? isWinnerPlayerA(resultFromA)
                  : null;

                const match = {
                  match_id: matchData.id,
                  match_number: matchData.match_number,
                  matchKey: matchInfo.matchKey,
                  result: pRez,
                  resultFromA,
                  resultFromB,
                  winner_id: hasResult
                    ? isWinner
                      ? challenger.user_id
                      : opponentPlayer.user_id
                    : '',
                  loser_id: hasResult
                    ? isWinner
                      ? opponentPlayer.user_id
                      : challenger.user_id
                    : '',
                  tournament_stage,
                  tournament_gender,
                  tournament_level,
                  tournament_id,
                  playerA: challenger,
                  playerB: opponentPlayer,
                };

                openEditModal(match);
              } catch (err) {
                console.error('Failed to fetch match data:', err);
                errorToastRef.current?.showError(
                  'Nepavyko atidaryti rezultato lango',
                );
              }
            }}
          />
        )}
      </Box>
    </>
  );
};

// Custom comparison so React.memo skips re-renders caused by parent state
// changes that don't actually affect what this card displays. We compare the
// identifying / display props and deliberately ignore function props
// (fetchMainTable) and the heavy object/array props (fullRow, allPlayers) —
// those get fresh references on every parent render but only matter when the
// user opens the edit modal, by which time the latest closures are captured.
const areCardPropsEqual = (prev, next) =>
  prev.pRez === next.pRez &&
  prev.first_name === next.first_name &&
  prev.last_name === next.last_name &&
  prev.winner === next.winner &&
  prev.user_id === next.user_id &&
  prev.doubles_partner === next.doubles_partner &&
  prev.partner_first_name === next.partner_first_name &&
  prev.partner_last_name === next.partner_last_name &&
  prev.isChallenger === next.isChallenger &&
  prev.avatar === next.avatar &&
  prev.matchKey === next.matchKey &&
  prev.playerGroup === next.playerGroup &&
  prev.fullMatchKey === next.fullMatchKey &&
  prev.tournament_gender === next.tournament_gender &&
  prev.tournament_level === next.tournament_level &&
  prev.tournament_stage === next.tournament_stage &&
  prev.tournament_id === next.tournament_id;

export default React.memo(TournamentPlayerCard, areCardPropsEqual);
